import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // In production, you might want to log this to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center px-4">
          <div className="text-center max-w-2xl mx-auto">
            {/* Error Illustration */}
            <div className="mb-8">
              <div className="w-32 h-32 mx-auto mb-6">
                <svg className="w-full h-full text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>

            {/* Error Message */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-medium text-gray-900 mb-4 sm:mb-6">
              Something went wrong
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
              We're sorry, but something unexpected happened. Our team has been notified and is working to fix the issue.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12">
              <button 
                onClick={() => window.location.reload()} 
                className="bg-brand-blue text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-normal hover:bg-brand-orange transition-all duration-300 text-sm sm:text-base"
              >
                Reload Page
              </button>
              <button 
                onClick={() => window.location.href = '/'} 
                className="bg-white text-brand-blue border-2 border-brand-blue px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-normal hover:bg-brand-blue hover:text-white transition-all duration-300 text-sm sm:text-base"
              >
                Go Home
              </button>
            </div>

            {/* Development Error Details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="border-t border-gray-200 pt-6 sm:pt-8 text-left">
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-4 sm:mb-6">
                  Error Details (Development Only)
                </h3>
                <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-800 overflow-auto max-h-64">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-2">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="border-t border-gray-200 pt-6 sm:pt-8">
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                If this problem persists, please contact us:
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <a 
                  href="mailto:info@curiye.com" 
                  className="text-sm sm:text-base text-brand-blue hover:text-brand-orange transition-colors duration-300"
                >
                  info@curiye.com
                </a>
                <a 
                  href="https://wa.me/252618715418" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm sm:text-base text-brand-blue hover:text-brand-orange transition-colors duration-300"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
